import { type SideBySideConsultNoteCandidate } from '@heidi/session/contracts';

import { ExperimentName } from '@/models/analytics/features/experiments';
import { createExperimentProps } from '@/utils/experimentNameConverter';
import { produce } from 'immer';
import { atom, getDefaultStore } from 'jotai';
import { atomFamily } from 'jotai-family';
import { type SideBySidePhase } from '../generation/side-by-side-phase';
import {
  SideBySideStreamStatus,
  type SideBySideConsultNoteCandidateWithStream,
  type SideBySideConsultNoteFreeTrialPromotion,
} from '../types';

export type SideBySideSessionState = {
  generationId: number | null;
  phase: SideBySidePhase;
  candidates: SideBySideConsultNoteCandidateWithStream[];
  setupLoading: boolean;
  rawExperimentNameGenerationMode: string;
  freeTrialPromotion: SideBySideConsultNoteFreeTrialPromotion | null;
  noteDisplayedAnalyticsSent: boolean;
  noteDisplayedTime: Date | null;
  postActionsCompleted: boolean;
  expiresAt: number | null;
};

export const createInitialSideBySideSessionState = (): SideBySideSessionState => ({
  generationId: null,
  phase: 'idle',
  candidates: [],
  setupLoading: false,
  rawExperimentNameGenerationMode: '',
  freeTrialPromotion: null,
  noteDisplayedAnalyticsSent: false,
  noteDisplayedTime: null,
  postActionsCompleted: false,
  expiresAt: null,
});

const knownSideBySideSessionIds = new Set<string>();

export const sideBySideSessionStateAtomFamily = atomFamily((sessionId: string) => {
  knownSideBySideSessionIds.add(sessionId);
  return atom<SideBySideSessionState>(createInitialSideBySideSessionState());
});

const unlinkParentSignalByController = new WeakMap<AbortController, () => void>();

const createCandidateAbortController = (parentSignal?: AbortSignal) => {
  const controller = new AbortController();
  if (!parentSignal) return controller;

  const abortFromParent = () => controller.abort(parentSignal.reason);
  if (parentSignal.aborted) {
    abortFromParent();
  } else {
    parentSignal.addEventListener('abort', abortFromParent, { once: true });
    unlinkParentSignalByController.set(controller, () => {
      parentSignal.removeEventListener('abort', abortFromParent);
    });
  }
  return controller;
};

export const disposeCandidateAbortController = (
  controller: AbortController,
  abort = false
) => {
  unlinkParentSignalByController.get(controller)?.();
  unlinkParentSignalByController.delete(controller);
  if (abort && !controller.signal.aborted) controller.abort();
};

const disposeCandidates = (
  candidates: SideBySideConsultNoteCandidateWithStream[],
  abort: boolean
) => {
  candidates.forEach((candidate) => {
    disposeCandidateAbortController(candidate.abortController, abort);
  });
};

export const clearSideBySideSessionState = (
  sessionId: string,
  jotaiStore: ReturnType<typeof getDefaultStore> = getDefaultStore()
) => {
  const stateAtom = sideBySideSessionStateAtomFamily(sessionId);
  disposeCandidates(jotaiStore.get(stateAtom).candidates, true);
  jotaiStore.set(stateAtom, createInitialSideBySideSessionState());
  sideBySideSessionStateAtomFamily.remove(sessionId);
  knownSideBySideSessionIds.delete(sessionId);
};

export const clearAllSideBySideSessionStates = (
  jotaiStore: ReturnType<typeof getDefaultStore> = getDefaultStore()
) => {
  [...knownSideBySideSessionIds].forEach((sessionId) => {
    clearSideBySideSessionState(sessionId, jotaiStore);
  });
};

export type SetNoteCandidatesInput =
  | SideBySideConsultNoteCandidate[]
  | {
      candidates: SideBySideConsultNoteCandidate[];
      parentSignal?: AbortSignal;
    };

type CandidateRecipe = (draft: SideBySideConsultNoteCandidateWithStream[]) => void;

export const internalNoteCandidatesAtomFamily = atomFamily((sessionId: string) =>
  atom(
    (get) => get(sideBySideSessionStateAtomFamily(sessionId)).candidates,
    (get, set, update: CandidateRecipe | SideBySideConsultNoteCandidateWithStream[]) => {
      const stateAtom = sideBySideSessionStateAtomFamily(sessionId);
      const current = get(stateAtom);
      set(stateAtom, {
        ...current,
        candidates: Array.isArray(update) ? update : produce(current.candidates, update),
      });
    }
  )
);

export const noteCandidatesAtomFamily = atomFamily((sessionId: string) =>
  atom(
    (get) => get(sideBySideSessionStateAtomFamily(sessionId)).candidates,
    (get, set, input: SetNoteCandidatesInput) => {
      const stateAtom = sideBySideSessionStateAtomFamily(sessionId);
      const current = get(stateAtom);
      const { candidates, parentSignal } = Array.isArray(input)
        ? { candidates: input, parentSignal: undefined }
        : input;

      disposeCandidates(current.candidates, true);
      const candidatesWithMetadata = candidates.map(
        (candidate): SideBySideConsultNoteCandidateWithStream => ({
          ...candidate,
          stream: [],
          abortController: createCandidateAbortController(parentSignal),
          status: SideBySideStreamStatus.WAITING_START,
          hovered: false,
          selected: false,
          confirmed: false,
          timings: {
            startTime: null,
            endTime: null,
            firstChunkTime: null,
            mostRecentChunkTime: null,
          },
          feedback: {
            positiveFeedback: '',
            negativeFeedback: '',
          },
        })
      );

      set(stateAtom, { ...current, candidates: candidatesWithMetadata });
    }
  )
);

const fieldAtomFamily = <K extends keyof SideBySideSessionState>(field: K) =>
  atomFamily((sessionId: string) =>
    atom(
      (get) => get(sideBySideSessionStateAtomFamily(sessionId))[field],
      (get, set, value: SideBySideSessionState[K]) => {
        const stateAtom = sideBySideSessionStateAtomFamily(sessionId);
        const current = get(stateAtom);
        set(stateAtom, { ...current, [field]: value });
      }
    )
  );

export const rawExperimentNameGenerationModeAtomFamily = fieldAtomFamily(
  'rawExperimentNameGenerationMode'
);
export const sideBySideSetupLoadingAtomFamily = fieldAtomFamily('setupLoading');
export const sideBySideFreeTrialPromotionAtomFamily =
  fieldAtomFamily('freeTrialPromotion');
export const noteDisplayedAnalyticsSentAtomFamily = fieldAtomFamily(
  'noteDisplayedAnalyticsSent'
);
export const noteDisplayedTimeAtomFamily = fieldAtomFamily('noteDisplayedTime');

export const shouldFallbackToSingleNoteViewAtomFamily = atomFamily((sessionId: string) =>
  atom((get) => {
    const candidates = get(internalNoteCandidatesAtomFamily(sessionId));
    return (
      candidates.length > 1 &&
      candidates.filter((candidate) => candidate.status !== SideBySideStreamStatus.ERROR)
        .length === 1
    );
  })
);

export const shouldDoSideBySideGenerationAtomFamily = atomFamily((sessionId: string) =>
  atom(
    (get) =>
      get(internalNoteCandidatesAtomFamily(sessionId)).length > 1 &&
      !get(shouldFallbackToSingleNoteViewAtomFamily(sessionId))
  )
);

export const shouldShowSideBySideNotesAtomFamily = atomFamily((sessionId: string) =>
  atom((get) => {
    const candidates = get(internalNoteCandidatesAtomFamily(sessionId));
    return (
      get(shouldDoSideBySideGenerationAtomFamily(sessionId)) &&
      candidates.every((candidate) =>
        [
          SideBySideStreamStatus.STREAMING,
          SideBySideStreamStatus.DONE,
          SideBySideStreamStatus.ERROR,
        ].includes(candidate.status)
      )
    );
  })
);

export const shouldShowSideBySideLoadingAtomFamily = atomFamily((sessionId: string) =>
  atom((get) => {
    return (
      get(sideBySideSetupLoadingAtomFamily(sessionId)) ||
      (get(shouldDoSideBySideGenerationAtomFamily(sessionId)) &&
        get(internalNoteCandidatesAtomFamily(sessionId)).some(
          (candidate) => candidate.status === SideBySideStreamStatus.WAITING_START
        ))
    );
  })
);

export const typedExperimentNameGenerationModeAtomFamily = atomFamily(
  (sessionId: string) =>
    atom((get) =>
      createExperimentProps(get(rawExperimentNameGenerationModeAtomFamily(sessionId)))
    )
);

export const isModelComparisonAtomFamily = atomFamily((sessionId: string) =>
  atom(
    (get) =>
      get(typedExperimentNameGenerationModeAtomFamily(sessionId)).experiment_name ===
      ExperimentName.ModelComparison
  )
);

export const sideBySideAllDoneOrErroredAtomFamily = atomFamily((sessionId: string) =>
  atom((get) =>
    get(internalNoteCandidatesAtomFamily(sessionId)).every((candidate) =>
      [SideBySideStreamStatus.ERROR, SideBySideStreamStatus.DONE].includes(
        candidate.status
      )
    )
  )
);
